import { Collapse } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import { useCallback } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
  BEAT_EVENT_LIST,
  ECG_EVENT_MENU_OPTIONS,
  EVENT_COUNT_CONVERT,
  ID_BOOKMARK_TAB,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import {
  BookmarkIcon as bookmarkIcon,
  IconArrowCollapse as arrowCollapse,
  IconArrowExpand as arrowExpand,
  OverviewCircularProgress,
  UnappliedChangesIcon as unappliedChangesIcon,
} from '@/shared/ui';
import ButtonAddNewEvent from './buttonAddNewEvent';
import useLeftColumn from './useLeftColumn';

const { Panel } = Collapse;

/**
 * Draws the collapse panel's chevron in the ECG events left column. Swaps between the expanded and collapsed arrow images according to the panel's active state.
 *
 * @param isActive - True while the panel is expanded.
 * @returns The arrow image for the panel's current state.
 */
const renderExpandIcon = ({ isActive }: any) => (
  <img src={isActive ? arrowExpand : arrowCollapse} alt="arrow icon" />
);

/**
 * Left navigation column of the ECG events tab, listing beat events, rhythm events and bookmarks. Renders the ventricular and supraventricular beat menus and the rhythm menu inside collapsible panels highlighted when they own the active button, then a bookmark tab whose count is capped at 99+ with a tooltip carrying the true number and an unapplied-changes marker while deletions are pending.
 *
 * @returns The left column with its event panels, bookmark tab and the add-new-event button.
 */
const LeftColumn = () => {
  const {
    activeButton,
    onClickTab,
    ecgBookmarksLength,
    ecgBookmarkDeleteIds,
    eventsInfo,
    hasGroupTypeChangeTabInfos,
    hasUnappliedChangeTabInfos,
    groupEventChanges,
    eventChanges,
  } = useLeftColumn();

  /**
   * Builds the beat-event buttons of the rhythm events left column, one per menu option matching the given subtype. Each button carries its review progress ring, label, event count capped at 99 plus with a tooltip holding the exact figure, and an unapplied-changes icon when that event type has pending single or group changes.
   *
   * @param subtype - Beat event subtype the menu options are filtered by; defaults to an empty string.
   * @returns A memoized callback producing the beat-event menu buttons.
   */
  const renderBeatEventMenu = useCallback(
    (subtype = '') =>
      _.map(
        _.filter(ECG_EVENT_MENU_OPTIONS, (x) => x.subtype === subtype),
        (d: any, i) => {
          const eventType = d.value;
          const formatValue = (EVENT_COUNT_CONVERT as any)[eventType];
          const eventCount = (eventsInfo as any)[formatValue].count;
          const groupTypeChange = !!hasGroupTypeChangeTabInfos[eventType];
          return (
            <button
              id={eventType}
              name={eventType}
              className={classnames(
                'templates-btn flex items-center',
                activeButton === eventType ? '--active' : '',
              )}
              onClick={() => {
                onClickTab(eventType);
              }}
            >
              <OverviewCircularProgress
                isActive={activeButton === eventType}
                name={`beatEvent-${subtype}`}
                index={i}
                value={(eventsInfo as any)[formatValue].progress}
              />
              {d.label}
              <div className="ml-auto" id={`eventCountRhythmEvents${eventType}`}>
                <div className="event-count">{eventCount > 99 ? '99+' : eventCount}</div>
                {eventCount > 99 && (
                  <UncontrolledTooltip
                    className="custom-tooltip"
                    target={`eventCountRhythmEvents${eventType}`}
                    placement="bottom-end"
                    delay={{ show: 0, hide: 0 }}
                    hideArrow
                  >
                    {eventCount}
                  </UncontrolledTooltip>
                )}
              </div>
              {(!!hasUnappliedChangeTabInfos[eventType] || groupTypeChange) && (
                <img
                  src={unappliedChangesIcon}
                  alt="Unapplied changes icon"
                  className="unapplied-changes-indicator"
                />
              )}
            </button>
          );
        },
      ),
    [groupEventChanges, eventsInfo, activeButton, eventChanges],
  );

  /**
   * Builds the rhythm-event buttons of the rhythm events left column from every menu option of type rhythm. Each button carries its review progress ring, label, event count capped at 99 plus with a tooltip holding the exact figure, and an unapplied-changes icon when that event type has pending single or group changes.
   *
   * @returns A memoized callback producing the rhythm-event menu buttons.
   */
  const renderRhythmEventMenu = useCallback(
    () =>
      _.map(
        _.filter(ECG_EVENT_MENU_OPTIONS, (x) => x.type === 'rhythm'),
        (d: any, i) => {
          const eventType = d.value;
          const formatValue = (EVENT_COUNT_CONVERT as any)[eventType];
          const eventCount = (eventsInfo as any)[formatValue].count;
          const groupTypeChange = !!hasGroupTypeChangeTabInfos[eventType];
          return (
            <button
              id={eventType}
              name={eventType}
              className={classnames(
                'templates-btn flex items-center',
                activeButton === eventType ? '--active' : '',
              )}
              onClick={() => {
                onClickTab(eventType);
              }}
            >
              <OverviewCircularProgress
                isActive={activeButton === eventType}
                index={i}
                name="rhythmEvent"
                value={(eventsInfo as any)[formatValue].progress}
              />
              {d.label}
              <div className="ml-auto" id={`eventCountRhythmEvents${eventType}`}>
                <div className="event-count">{eventCount > 99 ? '99+' : eventCount}</div>
                {eventCount > 99 && (
                  <UncontrolledTooltip
                    className="custom-tooltip"
                    target={`eventCountRhythmEvents${eventType}`}
                    placement="bottom-end"
                    delay={{ show: 0, hide: 0 }}
                    hideArrow
                  >
                    {eventCount}
                  </UncontrolledTooltip>
                )}
              </div>
              {(!!hasUnappliedChangeTabInfos[eventType] || groupTypeChange) && (
                <img
                  src={unappliedChangesIcon}
                  alt="Unapplied changes icon"
                  className="unapplied-changes-indicator"
                />
              )}
            </button>
          );
        },
      ),
    [groupEventChanges, eventsInfo, activeButton, eventChanges],
  );

  return (
    <div id="left-column" className="left-column">
      <div className="left-column-tab">
        <Collapse defaultActiveKey={['1']} expandIconPosition="start" expandIcon={renderExpandIcon}>
          <Panel
            header="Beat events"
            key="1"
            className={classnames(
              'panel-header',
              BEAT_EVENT_LIST.includes(activeButton as any) ? '--active' : '',
            )}
          >
            <div className="sub-header">Ventricular</div>
            <div className="templates-btn-container">{renderBeatEventMenu('ve')}</div>
            <div className="sub-header">Supraventricular</div>
            <div className="templates-btn-container">{renderBeatEventMenu('sve')}</div>
          </Panel>
        </Collapse>
        <div className="left-column-divider" />
        <Collapse defaultActiveKey={['2']} expandIconPosition="start" expandIcon={renderExpandIcon}>
          <Panel
            header="Rhythm events"
            key="2"
            className={classnames(
              'panel-header',
              Object.keys(TYPE_RHYTHM_EVENT_ENUM).includes(activeButton as any) ? '--active' : '',
            )}
          >
            <div className="templates-btn-container">{renderRhythmEventMenu()}</div>
          </Panel>
        </Collapse>
        <div className="left-column-divider" />
        <div>
          <button
            id={ID_BOOKMARK_TAB}
            name={ID_BOOKMARK_TAB}
            className={classnames(
              'templates-btn flex items-center',
              (activeButton as any) === ID_BOOKMARK_TAB ? '--active' : '',
            )}
            onClick={() => {
              onClickTab(ID_BOOKMARK_TAB);
            }}
          >
            <img className="complete-review" src={bookmarkIcon} alt="Bookmark icon" />
            Bookmarks
            <div className="ml-auto" id="bookmarkRhythmEvents">
              <div className="event-count">
                {ecgBookmarksLength > 99 ? '99+' : ecgBookmarksLength}
              </div>
              {ecgBookmarksLength > 99 && (
                <UncontrolledTooltip
                  className="custom-tooltip"
                  target="bookmarkRhythmEvents"
                  placement="bottom-end"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                >
                  {ecgBookmarksLength}
                </UncontrolledTooltip>
              )}
            </div>
            {ecgBookmarkDeleteIds.length > 0 && (
              <img
                src={unappliedChangesIcon}
                alt="Unapplied changes icon"
                className="unapplied-changes-indicator"
              />
            )}
          </button>
        </div>
      </div>
      <ButtonAddNewEvent />
    </div>
  );
};

export default LeftColumn;
